import { LOADER_POSITIONS } from '../../../utils/staffPositions';
import { isSiteUnavailableInSetup } from '../siteTableUtils';

export const DEFAULT_VIEW = { group: 'loader', search: '', region: 'all', statuses: ['Confirmed'], allocation: 'all', gap: '' };
export const GAP_LABELS = {
  allocated: 'Loaders allocated', generics: 'Generic placeholders', noStaff: 'Loaders with no staff',
  noPlow: 'Loaders with no plow', under: 'Sites under capacity',
  over: 'Sites over capacity', availableStaff: 'Staff with no loader',
};
const key = (id) => String(id);
const unique = (items, field) => [...new Map(items.map(item => [key(item[field]), item])).values()];
const sort = (items, field) => [...items].sort((a, b) => String(a[field] || '').localeCompare(String(b[field] || ''), undefined, { numeric: true, sensitivity: 'base' }));
export const isGeneric = v => v.vehicle_model === 'Generic Model';
export const isAllocated = v => Boolean(v.sites.length || v.allocated_sites?.length);
// Only working (allocated) named loaders need staff and a plow; idle and generic loaders are not gaps.
export const needsStaff = v => !v.archived && !isGeneric(v) && isAllocated(v) && !v.assigned_staff.length;
export const needsPlow = v => !v.archived && !isGeneric(v) && isAllocated(v) && !v.assigned_plow;
export const isAvailableStaff = s => !s.archived && LOADER_POSITIONS.includes(s.position) && !s.loaders.length
  && !s.assigned_bool && !s.assigned_loader && !s.assigned_walkway_crew_bool && !s.assigned_salting_route_bool;
export const capacityState = s => !(Number(s.parking_lots_sqft) > 0) ? 'unknown'
  : Number(s.parking_lots_sqft_allocated || 0) < Number(s.parking_lots_sqft) ? 'under'
    : Number(s.parking_lots_sqft_allocated || 0) > Number(s.parking_lots_sqft) ? 'over' : 'balanced';
export const siteInScope = (s, statuses) => s.historical || statuses.includes(s.contract_status);

export function normalizeOverview(sites, vehicles, staff) {
  const siteList = sort(unique(sites, 'site_id'), 'site_name').map(s => ({ ...s, loaders: [] }));
  const vehicleList = sort(unique(vehicles, 'vehicle_id'), 'vehicle_name').map(v => ({ ...v, sites: [], assigned_plow: v.assigned_plow ? { ...v.assigned_plow } : null, assigned_staff: unique(v.assigned_staff || [], 'staff_id') }));
  const vehicleMap = new Map(vehicleList.map(v => [key(v.vehicle_id), v]));
  siteList.forEach(s => (s.vehicles_lots || []).forEach(edge => {
    const v = vehicleMap.get(key(edge.vehicle_id));
    if (v && !s.loaders.includes(v)) { s.loaders.push(v); v.sites.push(s); }
  }));
  const staffList = sort(unique([...vehicleList.flatMap(v => v.assigned_staff), ...staff], 'staff_id'), 'name')
    .map(s => ({ ...s, loaders: vehicleList.filter(v => v.assigned_staff.some(p => key(p.staff_id) === key(s.staff_id))) }));
  return { sites: siteList, vehicles: vehicleList, staff: staffList };
}

export function overviewCounts(data, statuses = DEFAULT_VIEW.statuses) {
  const eligible = data.vehicles.filter(v => !v.archived);
  const named = eligible.filter(v => !isGeneric(v));
  const sites = data.sites.filter(s => siteInScope(s, statuses) && !isSiteUnavailableInSetup(s));
  return {
    total: eligible.length, allocated: eligible.filter(isAllocated).length,
    generics: eligible.filter(isGeneric).length, allocatedGenerics: eligible.filter(v => isGeneric(v) && isAllocated(v)).length,
    noStaff: named.filter(needsStaff).length,
    noPlow: named.filter(needsPlow).length,
    under: sites.filter(s => capacityState(s) === 'under').length,
    over: sites.filter(s => capacityState(s) === 'over').length,
    availableStaff: data.staff.filter(isAvailableStaff).length,
  };
}

const label = (name, unavailable) => `${name}${unavailable ? ' (Unavailable)' : ''}`;
export const siteLabel = s => label(s.site_name || `Site ${s.site_id}`, isSiteUnavailableInSetup(s));
export const loaderLabel = v => label(`${v.vehicle_name || `Loader ${v.vehicle_id}`}${isGeneric(v) ? ' (Generic)' : ''}`, v.archived);
export const staffLabel = s => label(s.name || `Staff ${s.staff_id}`, s.archived);
export const plowLabel = v => v?.assigned_plow ? label([
  v.assigned_plow.plow_name || `Plow ${v.assigned_plow.plow_id}`,
  [...new Set([v.assigned_plow.plow_model, v.assigned_plow.plow_size].filter(Boolean))].join(' · '),
  v.assigned_plow.is_confirmed === true ? 'Confirmed' : v.assigned_plow.is_confirmed === false ? 'Unconfirmed' : 'Confirmation unknown',
].filter(Boolean).join(' · '), v.assigned_plow.archived) : 'No plow';
export const ddLabel = v => v?.assigned_doubleDown ? label([
  v.assigned_doubleDown.doubleDown_name || `DoubleDown ${v.assigned_doubleDown.doubleDown_id}`,
  v.assigned_doubleDown.doubleDown_model, v.assigned_doubleDown.doubleDown_type_name,
].filter(Boolean).join(' · '), v.assigned_doubleDown.archived) : 'No DoubleDown';
export const capacityLabel = s => capacityState(s) === 'unknown' ? 'Capacity not specified'
  : `${Number(s.parking_lots_sqft_allocated || 0).toLocaleString()} / ${Number(s.parking_lots_sqft).toLocaleString()} sq. ft (${capacityState(s)})`;

function row(id, section, vehicles, sites, staff) {
  return { key: id, section, vehicles, sites, people: staff,
    loader: vehicles.map(v => `${loaderLabel(v)}${v.vehicle_subtype ? ` · ${v.vehicle_subtype}` : ''}`).join('\n') || 'No loader',
    site: sites.map(s => `${siteLabel(s)} · ${s.client_name || 'No customer'} · ${s.region || 'No region'} · ${s.contract_status || 'Unknown status'}`).join('\n') || 'No site',
    plow: vehicles.length ? vehicles.map(plowLabel).join('\n') : '—',
    doubleDown: vehicles.length ? vehicles.map(ddLabel).join('\n') : '—',
    staff: staff.map(staffLabel).join('\n') || 'No staff',
    capacity: sites.map(s => `${s.site_name}: ${capacityLabel(s)}`).join('\n') || '—',
    assignment: vehicles.map(v => [v.sites.length > 1 ? `Shared across ${v.sites.length} sites` : '', !isAllocated(v) ? 'Unallocated loader' : ''].filter(Boolean).join(' · ')).filter(Boolean).join('\n')
      || (staff.some(s => s.assigned_walkway_crew_bool || s.assigned_salting_route_bool) ? 'Assigned to another role' : ''),
  };
}

export function overviewRows(data, view = DEFAULT_VIEW) {
  const scopedSites = data.sites.filter(s => siteInScope(s, view.statuses));
  const visibleVehicles = data.vehicles.filter(v => !v.sites.length || v.sites.some(s => siteInScope(s, view.statuses)));
  let rows = [];
  if (view.group === 'site') {
    scopedSites.forEach(s => {
      if (!s.loaders.length) rows.push(row(`site:${s.site_id}:empty`, 'Sites without loaders', [], [s], []));
      s.loaders.forEach(v => rows.push(row(`site:${s.site_id}:${v.vehicle_id}`, siteLabel(s), [v], [s], v.assigned_staff)));
    });
    data.vehicles.filter(v => !v.sites.length).forEach(v => rows.push(row(`loader:${v.vehicle_id}`, 'Loaders without sites', [v], [], v.assigned_staff)));
  } else if (view.group === 'staff') {
    data.staff.forEach(s => {
      s.loaders.filter(v => visibleVehicles.includes(v)).forEach(v => rows.push(row(`staff:${s.staff_id}:${v.vehicle_id}`, staffLabel(s), [v], v.sites, [s])));
      if (!s.loaders.length) rows.push(row(`staff:${s.staff_id}`, isAvailableStaff(s) ? 'Available staff without loaders' : 'Other staff without loaders', [], [], [s]));
    });
    visibleVehicles.filter(v => !v.assigned_staff.length).forEach(v => rows.push(row(`loader:${v.vehicle_id}`, 'Loaders without staff', [v], v.sites, [])));
    scopedSites.filter(s => !s.loaders.length).forEach(s => rows.push(row(`site:${s.site_id}`, 'Sites without loaders', [], [s], [])));
  } else {
    visibleVehicles.forEach(v => rows.push(row(`loader:${v.vehicle_id}`, isAllocated(v) ? 'Allocated loaders' : 'Unallocated loaders', [v], v.sites, v.assigned_staff)));
    scopedSites.filter(s => !s.loaders.length).forEach(s => rows.push(row(`site:${s.site_id}`, 'Sites without loaders', [], [s], [])));
  }
  if (view.group !== 'staff') data.staff.filter(s => !s.loaders.length).forEach(s => rows.push(row(`staff:${s.staff_id}`, isAvailableStaff(s) ? 'Available staff without loaders' : 'Other staff without loaders', [], [], [s])));
  const matchesGap = r => {
    const vs = r.vehicles.filter(v => !v.archived);
    switch (view.gap) {
      case 'allocated': return vs.some(isAllocated);
      case 'generics': return vs.some(isGeneric);
      case 'noStaff': return vs.some(needsStaff);
      case 'noPlow': return vs.some(needsPlow);
      case 'under': case 'over': return r.sites.some(s => siteInScope(s, view.statuses) && !isSiteUnavailableInSetup(s) && capacityState(s) === view.gap);
      case 'availableStaff': return !r.vehicles.length && r.people.some(s => isAvailableStaff({ ...s, loaders: s.loaders || [] }));
      default: return true;
    }
  };
  return rows.filter(r => {
    // By site, each row is about its own site; elsewhere a loader row carries all of that loader's sites.
    const relatedSites = view.group === 'site' || !r.vehicles.length ? r.sites : unique(r.vehicles.flatMap(v => v.sites), 'site_id');
    const regionMatches = view.region === 'all' || (view.region === 'none' ? !relatedSites.length || relatedSites.some(s => !s.region) : relatedSites.some(s => s.region === view.region));
    const allocationMatches = view.allocation === 'all' || r.vehicles.some(v => isAllocated(v) === (view.allocation === 'allocated'));
    // Search covers what people read (names and identifiers), not internal database IDs.
    const searchText = [r.loader, r.site, r.plow, r.doubleDown, r.staff, ...relatedSites.flatMap(s => [siteLabel(s), s.client_name])].join(' ').toLowerCase();
    return regionMatches && allocationMatches && matchesGap(r) && searchText.includes(view.search.trim().toLowerCase());
  }).sort((a, b) => {
    const rank = r => r.section === 'Other staff without loaders' ? 5 : r.section === 'Available staff without loaders' ? 4
      : r.section === 'Sites without loaders' ? 3 : ['Unallocated loaders', 'Loaders without sites', 'Loaders without staff'].includes(r.section) ? 2 : 0;
    return rank(a) - rank(b);
  });
}

export function readView(params) {
  return { ...DEFAULT_VIEW,
    group: ['loader', 'site', 'staff'].includes(params.get('group')) ? params.get('group') : 'loader',
    search: params.get('search') || '', region: params.get('region') || 'all',
    statuses: params.has('statuses') ? params.get('statuses').split(',').filter(Boolean) : ['Confirmed'],
    allocation: ['allocated', 'unallocated'].includes(params.get('allocation')) ? params.get('allocation') : 'all',
    gap: Object.keys(GAP_LABELS).includes(params.get('gap')) ? params.get('gap') : '',
  };
}
export function viewParams(view) {
  return new URLSearchParams({ ...view, statuses: view.statuses.join(',') }).toString();
}

// Presentation helpers shared by the screen and the PDF so both read the same way.
export const setupStatusLabel = setup => !setup ? 'Loading' : setup.is_active ? 'Active'
  : setup.status === 'archived' ? 'Locked' : 'Draft';
export const capacityPercent = s => capacityState(s) === 'unknown' ? null
  : Math.round((Number(s.parking_lots_sqft_allocated || 0) / Number(s.parking_lots_sqft)) * 100);

export function countTiles(counts) {
  const warn = value => (value ? 'warn' : 'ok');
  return [
    { id: 'allocated', label: 'Loaders allocated', value: `${counts.allocated}/${counts.total}`,
      detail: counts.total ? `${Math.round((counts.allocated / counts.total) * 100)}% of the fleet` : 'No loaders', tone: 'brand',
      progress: counts.total ? counts.allocated / counts.total : 0 },
    { id: 'generics', label: 'Generic placeholders', value: `${counts.allocatedGenerics}/${counts.generics}`, detail: 'allocated / total', tone: 'info' },
    { id: 'noStaff', label: 'Loaders with no staff', value: counts.noStaff, detail: 'working named loaders', tone: warn(counts.noStaff) },
    { id: 'noPlow', label: 'Loaders with no plow', value: counts.noPlow, detail: 'working named loaders', tone: warn(counts.noPlow) },
    { id: 'under', label: 'Sites under capacity', value: counts.under, detail: 'below required area', tone: warn(counts.under) },
    { id: 'over', label: 'Sites over capacity', value: counts.over, detail: 'above required area', tone: counts.over ? 'danger' : 'ok' },
    { id: 'availableStaff', label: 'Staff with no loader', value: counts.availableStaff, detail: 'available loader staff', tone: counts.availableStaff ? 'info' : 'ok' },
  ];
}

// Splits ordered rows into consecutive sections for section headers.
export function sectionGroups(rows) {
  const groups = [];
  rows.forEach(r => {
    const last = groups[groups.length - 1];
    if (last && last.section === r.section) last.rows.push(r);
    else groups.push({ section: r.section, rows: [r] });
  });
  return groups;
}
